/**
 * ModerationWarning — branded inline banner shown when user-generated content
 * is blocked by moderation before it can be saved/published.
 *
 * One consistent look across every pre-publish surface outside chats (profile,
 * master bio, lives, video practices, library, community feed). Mirrors the
 * moderation visual language of `VoiceBlockedModal` — semantic error red,
 * `alert-circle`, calm Russian copy — but as an INLINE banner so it sits next
 * to the Save button and the user can edit the field in place and retry.
 *
 * Replaces both the raw `window.alert(...)` the moderation guard used to fire
 * and the old English card. Pure React Native (View/Text), theme-aware.
 *
 * Two input modes:
 *   - `violations` — structured categories from the client pre-check; renders a
 *     localized "Причина: …" line.
 *   - `message`    — a plain reason when only a backend 422 is available (no
 *     categories); falls back to the generic policy copy when omitted.
 */

import React from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './ui';
import { useTheme } from '../context/ThemeContext';
import { ModerationViolation, categoryLabelRu } from '../services/moderation';
import { MODERATION_BLOCK_MESSAGE } from '../services/errorMessages';

/** Semantic error red — same accent VoiceBlockedModal / the DS error token use. */
const ERROR = '#E53E3E';

interface ModerationWarningProps {
  /** Structured violations from the client pre-check (carry categories). */
  violations?: ModerationViolation[];
  /** Generic reason when only a backend 422 is available (no categories). */
  message?: string;
  onDismiss: () => void;
}

export const ModerationWarning: React.FC<ModerationWarningProps> = ({
  violations,
  message,
  onDismiss,
}) => {
  const { colors } = useTheme();

  // De-duplicated, localized category labels (e.g. «Насилие, Угрозы»).
  const categories = Array.from(
    new Set((violations ?? []).map((v) => categoryLabelRu(v.category)).filter(Boolean)),
  );

  return (
    <View style={styles.container}>
      <View style={styles.iconCircle}>
        <Ionicons name="alert-circle-outline" size={20} color={ERROR} />
      </View>

      <View style={styles.content}>
        <Typography style={[styles.title, { color: colors.textPrimary }]}>
          Не сохранено
        </Typography>
        <Typography style={[styles.body, { color: colors.textSecondary }]}>
          {message || MODERATION_BLOCK_MESSAGE}
        </Typography>
        {categories.length > 0 ? (
          <Typography style={[styles.reason, { color: ERROR }]}>
            {`Причина: ${categories.join(', ')}`}
          </Typography>
        ) : null}
      </View>

      <Pressable onPress={onDismiss} hitSlop={10} style={styles.close}>
        <Ionicons name="close" size={18} color={colors.textMuted} />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderWidth: 1,
    borderColor: 'rgba(229,62,62,0.25)',
    borderRadius: 16,
    backgroundColor: 'rgba(229,62,62,0.06)',
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginVertical: 12,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 2px 12px rgba(229,62,62,0.08)',
    }),
  } as any,
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(229,62,62,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    paddingTop: 1,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: -0.2,
    marginBottom: 3,
  },
  body: {
    fontSize: 13.5,
    lineHeight: 19,
  },
  reason: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 6,
  },
  close: {
    padding: 2,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
});

export default ModerationWarning;
